import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient, { TokenManager } from '../../../../shared/lib/apiClient';
import { useAuth } from '../../../../features/system/auth/hooks/useAuth';

const UserProfile: React.FC = () => {
  const { user, getCurrentAuthenticatedUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Password reset flow (via forgot-password + confirm-forgot-password)
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsFetchingProfile(true);
      setLoading(true);
      try {
        // Try to get user from context first
        const current = user || (await getCurrentAuthenticatedUser());
        if (current) {
          setEmail(current.email || '');
          // If Cognito returns a name attribute in getCurrentAuthenticatedUser, it should be included there
          setName((current as any).name || '');
          setAddress((current as any).address || '');
          // Try to fetch persisted profile from backend using user id
          const userId = (current as any).id || TokenManager.getUserId();
          if (userId) {
            try {
              const profile = await apiClient(`/userprofile/${userId}`, { requireAuth: true });
              if (profile) {
                setName(profile.name || (current as any).name || '');
                setAddress(profile.address || (current as any).address || '');
              }
            } catch (fetchErr: any) {
              // If profile not found (404) or other error, just ignore and keep cognito values
              console.debug('No persisted profile found or failed to fetch profile:', fetchErr?.message || fetchErr);
            }
          }
        }
      } catch (err: any) {
        console.error('Failed to load user', err);
      } finally {
        setLoading(false);
        setIsFetchingProfile(false);
      }
    };

    load();
  }, [user, getCurrentAuthenticatedUser]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      // Include user_id so backend can upsert the correct profile
      const userId = user?.id || TokenManager.getUserId();
      const body: any = { name, address };
      if (userId) body.user_id = userId;

      const result = await apiClient('/userprofile', {
        method: 'POST',
        body,
        requireAuth: true
      });

      setMessage(result.message || 'Profile updated');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetCode = async () => {
    setResetError(null);
    setResetMessage(null);
    try {
      await apiClient('/auth/forgot-password', {
        method: 'POST',
        body: { email },
        requireAuth: false
      });
      setResetCodeSent(true);
      setResetMessage('Reset code sent to your email');
    } catch (err: any) {
      setResetError(err.message || 'Failed to send reset code');
    }
  };

  const handleConfirmReset = async () => {
    setResetError(null);
    setResetMessage(null);
    try {
      await apiClient('/auth/confirm-forgot-password', {
        method: 'POST',
        body: { email, confirmation_code: resetCode, new_password: newPassword },
        requireAuth: false
      });
      setResetMessage('Password reset successfully. Please sign in with your new password.');
      // Optionally sign the user out so they can sign in again
    } catch (err: any) {
      setResetError(err.message || 'Failed to reset password');
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-gray-800 rounded">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => {
            // Prefer explicit 'from' path if provided when navigating to profile (see header),
            // otherwise fall back to browser history
            const from = (location.state as any)?.from;
            if (from) navigate(from);
            else navigate(-1);
          }}
          aria-label="Go back"
          className="inline-flex items-center px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
        >
          <span className="mr-2">←</span>
          <span className="text-sm">Back</span>
        </button>

        <h2 className="text-2xl font-bold text-white">Profile</h2>
      </div>

      {message && <div className="mb-4 text-green-400">{message}</div>}
      {error && <div className="mb-4 text-red-400">{error}</div>}

      {isFetchingProfile ? (
        <div className="space-y-4">
          <div className="h-6 w-1/3 bg-gray-600 rounded animate-pulse" />
          <div className="h-10 bg-gray-700 rounded animate-pulse" />
          <div className="h-10 bg-gray-700 rounded animate-pulse" />
          <div className="w-32 h-8 bg-gray-700 rounded animate-pulse" />
        </div>
      ) : (
        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Email</label>
            <input className="w-full px-3 py-2 rounded bg-gray-700 text-white" value={email} disabled />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">Name</label>
            <input
              className="w-full px-3 py-2 rounded bg-gray-700 text-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">Address</label>
            <input
              className="w-full px-3 py-2 rounded bg-gray-700 text-white"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Your address"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 rounded text-white"
            >
              {loading ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </form>
      )}

      <hr className="my-6 border-gray-700" />

      <h3 className="text-lg font-semibold text-white mb-2">Reset password</h3>
      {resetMessage && <div className="mb-2 text-green-400">{resetMessage}</div>}
      {resetError && <div className="mb-2 text-red-400">{resetError}</div>}

      {!resetCodeSent ? (
        <div className="space-y-2">
          <p className="text-sm text-gray-400">Click the button to send a password reset code to your email.</p>
          <button onClick={handleSendResetCode} className="px-3 py-2 bg-yellow-600 rounded text-white">Send reset code</button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Confirmation code</label>
            <input className="w-full px-3 py-2 rounded bg-gray-700 text-white" value={resetCode} onChange={(e) => setResetCode(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">New password</label>
            <input type="password" className="w-full px-3 py-2 rounded bg-gray-700 text-white" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          </div>
          <div>
            <button onClick={handleConfirmReset} className="px-3 py-2 bg-green-600 rounded text-white">Confirm new password</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
